import { Avatar, Icon, ChildMark } from "../components/Common.jsx";
import { childTasks, newTaskDefaultTokens, copyTaskToChildren } from "../lib/spendingDefaults.js";
import { MIN_REWARD_TOKENS, uid } from "../lib/constants.js";
import { t as ct } from "../lib/content.js";
const { useState } = React;

// The wizard's per-child earning tasks block.
//
// A NEW component rather than a change to AdjustSections' EarningActivitiesSection, and
// that is a deliberate scope decision: that section is classed reuse-verbatim and is what
// parent mode uses for ongoing management (award-on-tap pills, photo-proof, edit/delete).
// What the WIZARD needs is a different job — pick a couple of things fast, at sensible
// prices, without reading anything — so it gets its own surface and the shipped one is left
// alone. Both write the same `activities` rows.
//
// Quick-add chips exist because the honest alternative at setup time is an empty list and a
// blank text field, which is where wizards lose people. The value that arrives with a tap
// is the engine's, so a parent who taps three chips and never touches a number still ends
// up inside their cap.

const SUGGESTIONS = [
  { key: "read", icon: "ic-vocab", label: "Read a book" },
  { key: "tidy", icon: "ic-home", label: "Tidy bedroom" },
  { key: "chore", icon: "ic-medal", label: "Help around the house" },
  { key: "homework", icon: "ic-spelling", label: "Homework without being asked" },
];

function Stepper({ value, onCommit, label, testId }) {
  const [draft, setDraft] = useState(String(value));
  React.useEffect(() => { setDraft(String(value)); }, [value]);
  function commit(n) {
    const clamped = Math.max(MIN_REWARD_TOKENS, n);
    setDraft(String(clamped));
    if (clamped !== value) onCommit(clamped);
  }
  return (
    <span className="inline-flex items-center gap-1.5 flex-shrink-0">
      <button type="button" aria-label={`Decrease ${label}`} className="w-7 h-7 rounded-lg border border-slate-200 text-slate-500 text-base font-bold leading-none flex items-center justify-center" onClick={() => commit(value - 1)}>−</button>
      <input aria-label={label} data-testid={testId} inputMode="numeric"
        className="w-10 text-center border border-slate-200 rounded-lg py-1 text-xs font-bold text-slate-700"
        value={draft}
        onChange={(e) => setDraft(e.target.value.replace(/\D/g, ""))}
        onBlur={() => { const n = parseInt(draft, 10); if (Number.isNaN(n)) { setDraft(String(value)); return; } commit(n); }}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} />
      <button type="button" aria-label={`Increase ${label}`} className="w-7 h-7 rounded-lg border border-slate-200 text-slate-500 text-base font-bold leading-none flex items-center justify-center" onClick={() => commit(value + 1)}>+</button>
    </span>
  );
}

export function ChildTasksSection({ child, state, update }) {
  const [customOpen, setCustomOpen] = useState(false);
  const [customLabel, setCustomLabel] = useState("");
  const [copyFor, setCopyFor] = useState(null); // task id whose copy-to sheet is open
  const [copyFromOpen, setCopyFromOpen] = useState(false);
  const tasks = childTasks(state, child.id);
  const siblings = (state.profiles || []).filter((p) => p.id !== child.id);

  function addTask(label) {
    const tokens = newTaskDefaultTokens(child, state);
    update((s) => {
      if (!Array.isArray(s.activities)) s.activities = [];
      s.activities.push({ id: uid(), label, tokens, childId: child.id, linkKey: null });
      return s;
    });
  }
  function setTokens(id, tokens) {
    update((s) => { s.activities = s.activities.map((a) => (a.id === id ? { ...a, tokens } : a)); return s; });
  }
  function removeTask(id) {
    update((s) => { s.activities = s.activities.filter((a) => a.id !== id); return s; });
  }
  function copyTo(taskId, targetIds) {
    update((s) => { copyTaskToChildren(s, taskId, targetIds); return s; });
    setCopyFor(null);
  }
  // Copy FROM a sibling: the block-level half of the copy model. Sequential setup already
  // pre-stages the previous child's tasks, so this is for the parent who removed them and
  // changed their mind, or who is doing this later from a child added afterwards.
  function copyFrom(sourceChildId) {
    update((s) => {
      const sourceTasks = (s.activities || []).filter((a) => a.childId === sourceChildId);
      sourceTasks.forEach((t) => copyTaskToChildren(s, t.id, [child.id]));
      return s;
    });
    setCopyFromOpen(false);
  }

  const suggestionsLeft = SUGGESTIONS.filter((sug) => !tasks.some((t) => t.label === sug.label));

  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100" data-testid="child-tasks-section">
      <div className="flex items-center gap-2.5 mb-1">
        <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0"><Icon id="ic-medal" size={18} hue="gold" /></div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-slate-900">{ct("wizard.earn.tasks_title", "{name}'s tasks", { name: child.name })}</div>
          <p className="text-xs text-slate-500 leading-snug">
            {tasks.length === 0
              ? ct("wizard.earn.tasks_hint_empty", "Nothing yet. There's room in the cap for one. Tap to add:")
              : ct("wizard.earn.tasks_hint_avg", "New tasks are priced at the average of {name}'s current rewards.", { name: child.name })}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-1.5 mt-2.5">
        {tasks.map((t) => (
          <div key={t.id} className="flex items-center gap-2 bg-slate-50 border border-slate-100 rounded-xl px-2.5 py-2" data-testid="child-task-row">
            <div className="min-w-0 flex-1">
              <div className="text-xs font-bold text-slate-800 truncate">{t.label}</div>
              {siblings.length > 0 && (
                <button type="button" className="text-xs font-bold text-teal-600" data-testid="copy-task-to" onClick={() => setCopyFor(copyFor === t.id ? null : t.id)}>
                  {siblings.length === 1
                    ? ct("wizard.earn.copy_to", "Copy to {name}", { name: siblings[0].name })
                    : ct("wizard.earn.copy_to_picker_title", "Copy to…")}
                </button>
              )}
            </div>
            <Stepper value={t.tokens || MIN_REWARD_TOKENS} label={`${t.label} reward`} testId="child-task-tokens" onCommit={(n) => setTokens(t.id, n)} />
            <button type="button" aria-label={`Remove ${t.label}`} className="text-slate-300 text-sm font-bold px-1 leading-none" onClick={() => removeTask(t.id)}><Icon id="ic-close" size={14} style={{ color: "currentColor" }} /></button>
          </div>
        ))}
      </div>

      {/* Copy-to picker. One sibling gets a straight action above; two or more get this
          list, because guessing which sibling a parent meant is exactly the kind of helpful
          assumption that quietly gives the wrong child a reward. */}
      {copyFor && siblings.length > 0 && (
        <div className="mt-2 border border-slate-200 rounded-xl p-2.5" data-testid="copy-to-sheet">
          <div className="text-xs font-medium text-slate-500 mb-1.5">{ct("wizard.earn.copy_to_picker_title", "Copy to…")}</div>
          <div className="flex flex-wrap gap-1.5">
            {siblings.map((sib) => (
              <button key={sib.id} type="button" className="flex items-center gap-1.5 rounded border border-slate-200 px-2.5 py-1 text-xs font-bold text-slate-600" onClick={() => copyTo(copyFor, [sib.id])}>
                <ChildMark profile={sib} size="sm" /> {sib.name}
              </button>
            ))}
            {siblings.length > 1 && (
              <button type="button" className="rounded bg-slate-900 text-white px-2.5 py-1 text-xs font-bold" onClick={() => copyTo(copyFor, siblings.map((s) => s.id))}>
                {ct("wizard.earn.copy_to_all", "All children")}
              </button>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-1.5">{ct("wizard.earn.copy_sized_note", "Each copy is priced against that child's own cap.")}</p>
        </div>
      )}

      <div className="flex flex-wrap gap-1.5 mt-3">
        {suggestionsLeft.map((sug) => (
          <button key={sug.key} type="button" data-testid="task-quick-add"
            className="rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-bold px-2.5 py-1.5"
            onClick={() => addTask(sug.label)}>
            + <Icon id={sug.icon} size={12} className="inline-block align-[-2px] mx-0.5" style={{ color: "currentColor" }} /> {sug.label}
          </button>
        ))}
        <button type="button" className="rounded border border-slate-200 bg-white text-slate-600 text-xs font-bold px-2.5 py-1.5" onClick={() => setCustomOpen((o) => !o)}>
          {customOpen ? ct("wizard.common.cancel", "Cancel") : ct("wizard.earn.quick_add_own", "+ Own task")}
        </button>
        {siblings.length > 0 && (
          <button type="button" data-testid="copy-from-open" className="rounded border border-teal-200 bg-teal-50 text-teal-700 text-xs font-bold px-2.5 py-1.5" onClick={() => setCopyFromOpen((o) => !o)}>
            {siblings.length === 1
              ? ct("wizard.earn.copy_from", "Copy {name}'s tasks", { name: siblings[0].name })
              : ct("wizard.earn.copy_from_any", "Copy tasks from…")}
          </button>
        )}
      </div>

      {customOpen && (
        <div className="mt-2 flex items-center gap-2">
          <input className="border border-slate-200 rounded-lg px-3 py-2 text-xs flex-1 min-w-0" placeholder={ct("wizard.earn.own_task_placeholder", "What should they do?")}
            value={customLabel} onChange={(e) => setCustomLabel(e.target.value)} data-testid="own-task-input" />
          <button type="button" className="bg-slate-900 text-white text-xs font-bold rounded-lg px-3 py-2"
            onClick={() => { if (!customLabel.trim()) return; addTask(customLabel.trim()); setCustomLabel(""); setCustomOpen(false); }}>
            {ct("wizard.common.add", "Add")}
          </button>
        </div>
      )}

      {copyFromOpen && siblings.length > 0 && (
        <div className="mt-2 border border-slate-200 rounded-xl p-2.5" data-testid="copy-from-sheet">
          <div className="flex flex-wrap gap-1.5">
            {siblings.map((sib) => (
              <button key={sib.id} type="button" className="flex items-center gap-1.5 rounded border border-slate-200 px-2.5 py-1 text-xs font-bold text-slate-600" onClick={() => copyFrom(sib.id)}>
                <ChildMark profile={sib} size="sm" /> {sib.name}
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-1.5">{ct("wizard.earn.copy_from_note", "Anything already shared with them is skipped.")}</p>
        </div>
      )}
    </div>
  );
}
